import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectSetting, settingWriteActions } from './selectSetting';
import type { SettingKey, SettingValue } from './settingsRegistry';
import type { SettingContext, SettingsState } from './settingTypes';

/**
 * A setting's value through the cascade, and a setter that writes the player's
 * change to the right layers. `context` names the mode and the piece, exercise
 * or session for mode and instance settings; `authored` is the content's value.
 */
export function useSetting<K extends SettingKey>(
  key: K,
  context: SettingContext = {},
  authored?: SettingValue<K>,
): [SettingValue<K>, (value: SettingValue<K>) => void] {
  const dispatch = useDispatch();
  const { mode, instanceId } = context;
  // Shallow equality: a record value (the hands) is parsed into a new object on every read.
  const value = useSelector(
    (state: SettingsState) => selectSetting(state, key, { mode, instanceId }, authored),
    shallowEqual,
  );
  const setValue = React.useCallback(
    (next: SettingValue<K>) => {
      settingWriteActions(key, next, { mode, instanceId }).forEach((action) => dispatch(action));
    },
    [dispatch, key, mode, instanceId],
  );
  return [value, setValue];
}
